// @ts-nocheck
import React, { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useNavigate } from 'react-router-dom';
import { getAvatarURL } from '../../../utils/avatar.ts';
import { AiOutlineEdit } from 'react-icons/ai';
import { GoTrash } from 'react-icons/go';
import RecipeTagList from '../../ui/molecules/RecipeTagList.tsx';
import AddToMealPlanButton from '../../MealPlanner/AddToMealPlanButton.tsx';

interface RecipeCardSwipeableProps {
  recipe: any;
  onEdit?: () => void;
  onDelete?: (id: string) => void;
  isSwiped: boolean;
  onSwipe: () => void;
  onSwipeReset: () => void;
  swipeable?: boolean;
  onAddToPlan: () => void;
}

const RecipeCardSwipeable: React.FC<RecipeCardSwipeableProps> = ({
  recipe,
  onEdit,
  onDelete,
  isSwiped,
  onSwipe,
  onSwipeReset,
  swipeable = true,
  onAddToPlan,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();
  const fallbackImage = '/no-image.png';

  const handlers = useSwipeable({
    onSwipedLeft: () => swipeable && onSwipe?.(),
    onSwipedRight: () => swipeable && onSwipeReset?.(),
    delta: 30,
    trackTouch: true,
  });

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/app/edit-recipe/${recipe.id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    onDelete?.(recipe.id);
    setShowConfirm(false);
  };

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* behind-the-scenes Edit/Delete buttons */}
      {onEdit && onDelete && (
        <div className="absolute inset-0 flex space-x-2 justify-end items-center pr-1 bg-white z-0">
          <button
            onClick={handleEdit}
            className="p-2 border border-gray-300 rounded hover:bg-gray-100 text-gray-700"
          >
            <AiOutlineEdit className="w-5 h-5" />
          </button>
          <button
            onClick={handleDelete}
            className="p-2 border border-red-300 text-red-600 rounded hover:bg-red-50 hover:border-red-400"
          >
            <GoTrash className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* the swipeable + clickable card itself */}
      <div
        {...handlers}
        onClick={() => navigate(`/app/recipe/${recipe.id}`)}
        className={`bg-white rounded-xl border border-gray-300 hover:bg-gray-50
                    transition-transform duration-300 ease-in-out relative
                    w-full md:max-w-lg mx-auto flex ${
                      isSwiped ? '-translate-x-24' : 'translate-x-0'
                    }`}
      >
        {/* IMAGE: fixed 24×24 height */}
        <div className="w-24 h-24 flex-shrink-0 bg-gray-200 rounded-l overflow-hidden">
          {recipe.imageUrl ? (
            <img
              src={recipe.imageUrl}
              alt={recipe.title}
              className="object-cover w-full h-full"
              onError={(e) => {
                const t = e.currentTarget;
                t.onerror = null;
                t.src = fallbackImage;
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
              No Image
            </div>
          )}
        </div>

        {/* CONTENT: p-2, flex column */}
        <div className="p-2 flex flex-1 flex-col justify-between min-w-0">
          {/* title + creator */}
          <div>
            <h3 className="font-heading font-medium truncate">
              {recipe.title}
            </h3>
            <div className="flex items-center text-sm text-gray-600 mt-0.5">
              <img
                src={getAvatarURL(recipe.user)}
                alt="avatar"
                className="w-4 h-4 rounded-full mr-2"
              />
              <span className="truncate">{recipe.user?.name || 'Unknown'}</span>
            </div>
          </div>

          {/* tags (with fade) */}
          <div className="mt-2 flex items-center">
            <div className="relative flex-1 min-w-0">
              <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap min-w-0">
                <RecipeTagList
                  recipeTags={recipe.recipeTags}
                  context="search"
                />
              </div>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white" />
            </div>
            {/* 2) “+” button moved out and absolutely positioned, just like Wide */}
            <div className="absolute bottom-0 right-0 z-10">
              <AddToMealPlanButton
                variant="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToPlan();
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* confirm-delete modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Confirm Delete</h2>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this recipe?
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                className="px-4 py-2 rounded border text-gray-600 border-gray-400 hover:bg-gray-50"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeCardSwipeable;
